import { defineStore } from 'pinia';
import { useConnectionStore } from '@/stores/connection';
import type {
  Query,
  Mutation,
  Address,
  Event,
  SettlementDeal,
  CustomExpense,
  ManualTicketSale,
  Payment,
  SettlementTypeEnum,
  SettlementContract,
  SettlementAttachment,
  SettlementTaxWithholdingTypeEnum,
  AddressAttributes,
} from '@/generated/graphql';
import { ensureNumbers } from '@/helpers/ensureNumber';
import * as apolloCore from '@apollo/client/core';

const { gql } = apolloCore;

export interface eventArtistSettlement {
  address: Address[];
  availableEvents: Event[];
  barDeals: SettlementDeal[];
  businessName: string;
  createdAt: string;
  customExpenses: CustomExpense[];
  deals: SettlementDeal[];
  eventIds: number[];
  events: Event[];
  id: number;
  manualTicketSales: ManualTicketSale[];
  payments: Payment[];
  settlementType: SettlementTypeEnum;
  signedContracts: SettlementContract[];
  taxAttachments: SettlementAttachment[];
  taxId: string;
  taxWithholdingAmount: number;
  taxWithholdingType: SettlementTaxWithholdingTypeEnum;
  ticketDeals: SettlementDeal[];
  updatedAt: string;
}

interface SettlementSheetState {
  settlementSheet?: eventArtistSettlement;
  settlementSheetLoading: boolean;
}

const ADDRESS_FIELDS_FRAGMENT = gql`
  fragment AddressFields on Address {
    id
    city
    country
    state
    street1
    street2
    postalCode
  }
`;

const SETTLEMENT_SHEET_QUERY = gql`
  query eventArtistSettlement($settlementId: Int!) {
    eventArtistSettlement(id: $settlementId) {
      id
      settlementType
      businessName
      taxId
      taxWithholdingAmount
      taxWithholdingType
      taxAttachments {
        identifier
        url
      }
      address {
        id
        city
        country
        state
        street1
        street2
        postalCode
      }
      availableEvents {
        id
        date
        startTime
        doorTime
        endTime
        timezone
      }
      events {
        id
        date
        startTime
        doorTime
        endTime
      }
      customExpenses {
        id
        amount
        description
        due
        deductedType
      }
      barDeals {
        id
        dealType
        deal {
          key
          name
          comment
          calculation
        }
        amount
        percent
        expenseAmount
        hitAmount
        firstAmount
        attachments {
          identifier
          url
        }
      }
      ticketDeals {
        id
        dealType
        deal {
          key
          name
          comment
          calculation
        }
        amount
        percent
        expenseAmount
        hitAmount
        firstAmount
        showTicketSales
        attachments {
          identifier
          url
        }
      }
      eventIds
      manualTicketSales {
        id
        description
        price
        ticketsSold
      }
      payments {
        id
        description
        amount
        checkWireNr
        dateDue
        datePaid
        payee
        paymentType
        attachments {
          identifier
          url
        }
      }
    }
  }
`;

export const useSettlementSheetStore = defineStore('settlementSheet', {
  state: (): SettlementSheetState => ({
    settlementSheet: undefined,
    settlementSheetLoading: false,
  }),

  actions: {
    async fetchSettlementSheet(settlementId: number) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.settlementSheetLoading = true;

      try {
        const { data } = await apiClient.query<Query>({
          query: SETTLEMENT_SHEET_QUERY,
          variables: { settlementId },
        });
        this.settlementSheet = JSON.parse(JSON.stringify(data.eventArtistSettlement));
      } finally {
        this.settlementSheetLoading = false;
      }
    },

    async updateSelectedEvents(eventIds: number[]) {
      if (!this.settlementSheet) {
        return;
      }

      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      await apiClient.mutate<Mutation>({
        mutation: gql`
          mutation ($settlement: UpdateEventArtistSettlementInput!) {
            updateEventArtistSettlement(input: $settlement) {
              settlement {
                id
              }
              errors
            }
          }
        `,
        variables: {
          settlement: {
            id: this.settlementSheet.id,
            eventIds: eventIds,
          },
        },
      });
    },

    async saveManualTicket(item: ManualTicketSale) {
      if (!this.settlementSheet) {
        return;
      }

      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      const payload = ensureNumbers(item, 'price', 'ticketsSold');

      if (payload._destroy) {
        payload.price = 0;
        payload.ticketsSold = 0;
        payload.description = '';
      }

      await apiClient.mutate<Mutation>({
        mutation: gql`
          mutation ($settlement: UpdateEventArtistSettlementInput!) {
            updateEventArtistSettlement(input: $settlement) {
              clientMutationId
              settlement {
                manualTicketSales {
                  id
                  description
                  price
                  ticketsSold
                  _destroy
                }
              }
              errors
            }
          }
        `,
        variables: {
          settlement: {
            id: this.settlementSheet.id,
            manualTicketSales: [
              {
                id: item.id,
                price: parseFloat(item.price as any),
                ticketsSold: parseInt(item.ticketsSold as any),
                description: item.description,
                _destroy: item._destroy,
              },
            ],
          },
        },
      });
    },

    async saveCustomExpense(item: CustomExpense) {
      if (!this.settlementSheet) {
        return;
      }

      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      const payload = ensureNumbers(item, 'amount');

      if (payload._destroy) {
        payload.amount = 0;
        payload.description = '';
      }

      await apiClient.mutate<Mutation>({
        mutation: gql`
          mutation ($settlement: UpdateEventArtistSettlementInput!) {
            updateEventArtistSettlement(input: $settlement) {
              clientMutationId
              settlement {
                customExpenses {
                  id
                  amount
                  description
                  due
                  deductedType
                  _destroy
                }
              }
              errors
            }
          }
        `,
        variables: {
          settlement: {
            id: this.settlementSheet.id,
            customExpenses: [
              {
                id: item.id,
                amount: parseFloat(item.amount as any),
                due: item.due,
                deductedType: item.deductedType,
                description: item.description,
                _destroy: item._destroy,
              },
            ],
          },
        },
      });
    },

    async saveAddress(addressAttributes: AddressAttributes) {
      if (!this.settlementSheet) {
        return;
      }

      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      const { data } = await apiClient.mutate<Mutation>({
        mutation: gql`
          mutation ($settlement: UpdateEventArtistSettlementInput!) {
            updateEventArtistSettlement(input: $settlement) {
              clientMutationId
              settlement {
                address {
                  ...AddressFields
                }
              }
              errors
            }
          }
          ${ADDRESS_FIELDS_FRAGMENT}
        `,
        variables: {
          settlement: {
            id: this.settlementSheet.id,
            address: addressAttributes,
          },
        },
      });

      return data?.updateEventArtistSettlement?.settlement;
    },
  },
});
